import { useCallback, useEffect, useState, type FC } from "react";

import { decodeBase64ToBlob } from "@/api/utils/file";
import {
  PDFViewer,
  type PDFViewerPageOverlayProps
} from "@/components/extend/pdf-viewer";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  /** Base64 PDF from the portal's file routes. */
  data: string;
  label: string;
  /** A note shown on a page, e.g. where to sign it in ink. */
  pageNote?: (pageIndex: number) => string | null;
  onLoaded?: (pages: number) => void;
};

/** Every page at the panel's width, in a viewer tall enough to read on phones. */
export const PortalPdf: FC<Props> = ({ data, label, pageNote, onLoaded }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(decodeBase64ToBlob(data));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [data]);

  const noteFor = useCallback(
    (pageNumber: number) => pageNote?.(pageNumber - 1) ?? null,
    [pageNote]
  );

  const renderNote = useCallback(
    ({ pageNumber }: PDFViewerPageOverlayProps) => {
      const note = noteFor(pageNumber);
      return note ? (
        <span className="pointer-events-none absolute top-2 left-2 z-1 rounded-md bg-warn-soft px-2.5 py-1 text-xs font-medium text-warn shadow-popover">
          {note}
        </span>
      ) : null;
    },
    [noteFor]
  );

  return (
    <div
      className="h-[78dvh] min-h-96 w-full overflow-hidden rounded-md"
      role="document"
      aria-label={label}
    >
      {url ? (
        <PDFViewer
          key={url}
          src={url}
          defaultZoom="fit-width"
          showToolbar={false}
          showDownload={false}
          showUpload={false}
          showRotateControls={false}
          pageClassName={(pageNumber) =>
            noteFor(pageNumber) ? "ring-1 ring-warn-line" : undefined
          }
          renderPageOverlay={renderNote}
          onDocumentLoadSuccess={onLoaded}
        />
      ) : (
        <Skeleton className="aspect-[8.5/11] w-full rounded-md" />
      )}
    </div>
  );
};
